import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faClock,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useSendContactFormEmailMutation } from '../../services/emailService/__generated__/queries.generated';
import { toast } from 'react-toastify';

const PageContainer = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    py-12
    pb-8
    md:pb-16
    px-4
    bg-gray-900
    text-white
    box-border
  `}
`;

const ContentContainer = styled.div`
  ${tw`
    w-full
    max-w-full
    min-w-0
    max-w-screen-xl
    mx-auto
    box-border
  `}
`;

const PageTitle = styled.h1`
  ${tw`
    text-3xl
    md:text-4xl
    font-bold
    text-center
    mb-12
    text-white
  `}
`;

const ContactSection = styled.div`
  ${tw`
    flex
    flex-col
    lg:flex-row
    gap-12
    box-border
  `}
`;

const ContactInfoSection = styled.div`
  ${tw`
    w-full
    lg:w-1/3
    box-border
  `}
`;

const ContactFormSection = styled.div`
  ${tw`
    w-full
    lg:w-2/3
    bg-gray-800
    rounded-lg
    shadow-lg
    p-8
    box-border
  `}
`;

const InfoCard = styled.div`
  ${tw`
    bg-gray-800
    rounded-lg
    shadow-lg
    p-8
    mb-8
    box-border
  `}
`;

const InfoTitle = styled.h3`
  ${tw`
    text-xl
    font-bold
    mb-6
    text-white
  `}
`;

const InfoItem = styled.div`
  ${tw`
    flex
    items-start
    gap-4
    mb-6
    last:mb-0
  `}
`;

const IconWrapper = styled.div`
  ${tw`
    text-red-500
    mt-1
  `}
`;

const InfoContent = styled.div`
  ${tw`
    flex-1
  `}
`;

const InfoLabel = styled.h4`
  ${tw`
    font-semibold
    text-white
    mb-1
  `}
`;

const InfoText = styled.p`
  ${tw`
    text-gray-300
  `}
`;

const HoursGrid = styled.div`
  ${tw`
    grid
    grid-cols-2
    gap-2
  `}
`;

const HoursItem = styled.div`
  ${tw`
    text-gray-300
  `}
`;

const HoursDay = styled.span`
  ${tw`
    font-semibold
    text-white
  `}
`;

const FormTitle = styled.h3`
  ${tw`
    text-xl
    font-bold
    mb-6
    text-white
  `}
`;

const Form = styled.form`
  ${tw`
    space-y-6
    box-border
  `}
`;

const FormRow = styled.div`
  ${tw`
    flex
    flex-col
    md:flex-row
    gap-6
    box-border
  `}
`;

const FormGroup = styled.div`
  ${tw`
    flex-1
    box-border
  `}
`;

const Label = styled.label`
  ${tw`
    block
    text-sm
    font-medium
    text-gray-300
    mb-1
  `}
`;

const Input = styled.input`
  ${tw`
    w-full
    p-2
    border
    border-gray-700
    rounded
    bg-gray-700
    text-white
    focus:outline-none
    focus:ring-2
    focus:ring-red-500
  `}
`;

const TextArea = styled.textarea`
  ${tw`
    w-full
    p-2
    border
    border-gray-700
    rounded
    bg-gray-700
    text-white
    focus:outline-none
    focus:ring-2
    focus:ring-red-500
    h-32
  `}
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  ${tw`
    bg-red-600
    text-white
    py-3
    px-6
    rounded
    font-bold
    transition-colors
    duration-200
    w-full
    md:w-auto max-w-full min-w-0 box-border`}
  &:hover {
    ${tw`bg-red-700`}
  }
  ${({ disabled }) => (disabled ? tw`bg-gray-500 cursor-not-allowed` : tw`bg-red-600`)}
`;

const SuccessMessage = styled.div`
  ${tw`
    bg-gray-700
    border
    border-green-500
    text-green-400
    px-4
    py-3
    rounded
    flex
    items-center
    gap-2
    mb-6
  `}
`;

const MapContainer = styled.div`
  ${tw`
    w-full
    h-[400px]
    rounded-lg
    overflow-hidden
    mt-12
    box-border
  `}
`;

const DealershipContact = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });

  const [sendContactFormEmail, { loading, error }] = useSendContactFormEmailMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const formIsValid = useMemo(() => {
    return formData.firstName && formData.lastName && formData.email && formData.message;
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, you would send the form data to your backend
    setFormSubmitted(true);
    if (!formIsValid) {
      toast.error('Please fill out all fields');
      return;
    }
    try {
      await sendContactFormEmail({
        variables: {
          contactDealerInput: formData,
        },
      });
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
    } catch (error) {
      toast.error('Error sending contact form email');
    }
    // Reset form after submission
  };

  return (
    <PageContainer>
      <ContentContainer>
        <PageTitle>Contact Us</PageTitle>

        <ContactSection>
          <ContactInfoSection>
            <InfoCard>
              <InfoTitle>Contact Information</InfoTitle>

              <InfoItem>
                <IconWrapper>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </IconWrapper>
                <InfoContent>
                  <InfoLabel>Address</InfoLabel>
                  <InfoText>2150 N first Street, San Jose, CA 95131</InfoText>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <IconWrapper>
                  <FontAwesomeIcon icon={faPhone} />
                </IconWrapper>
                <InfoContent>
                  <InfoLabel>Phone</InfoLabel>
                  <InfoText>+1(866)217-2849</InfoText>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <IconWrapper>
                  <FontAwesomeIcon icon={faEnvelope} />
                </IconWrapper>
                <InfoContent>
                  <InfoLabel>Email</InfoLabel>
                  <InfoText>contact@autopilotee.com</InfoText>
                  <InfoText>contact@jidosojuholding.com</InfoText>
                </InfoContent>
              </InfoItem>
            </InfoCard>

            <InfoCard>
              <InfoTitle>Business Hours</InfoTitle>
              <InfoItem>
                <IconWrapper>
                  <FontAwesomeIcon icon={faClock} />
                </IconWrapper>
                <InfoContent>
                  <HoursGrid>
                    <HoursItem>
                      <HoursDay>Monday:</HoursDay> 10:00 AM - 2:00 PM
                    </HoursItem>
                    <HoursItem>
                      <HoursDay>Tuesday:</HoursDay> 10:00 AM - 2:00 PM
                    </HoursItem>
                    <HoursItem>
                      <HoursDay>Wednesday:</HoursDay> 10:00 AM - 2:00 PM
                    </HoursItem>
                    <HoursItem>
                      <HoursDay>Thursday:</HoursDay> 10:00 AM - 2:00 PM
                    </HoursItem>
                    <HoursItem>
                      <HoursDay>Friday:</HoursDay> 10:00 AM - 2:00 PM
                    </HoursItem>
                    <HoursItem>
                      <HoursDay>Saturday:</HoursDay> Appointment Only
                    </HoursItem>
                    <HoursItem>
                      <HoursDay>Sunday:</HoursDay> Appointment Only
                    </HoursItem>
                  </HoursGrid>
                </InfoContent>
              </InfoItem>
            </InfoCard>
          </ContactInfoSection>

          <ContactFormSection>
            <FormTitle>Send Us a Message</FormTitle>

            {formSubmitted && (
              <SuccessMessage>
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Your message has been sent successfully. We'll get back to you soon!</span>
              </SuccessMessage>
            )}

            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="message">Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={loading || !formIsValid}>
                {loading ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </Form>
          </ContactFormSection>
        </ContactSection>

        <MapContainer>
          {/* In a real app, you would use Google Maps or another map provider */}
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3170.2834969928456!2d-121.92248492393867!3d37.37703597208839!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fcbf5f6dd9f07%3A0x5b9a3e0c9c9c5a5a!2s2150%20N%201st%20St%2C%20San%20Jose%2C%20CA%2095131!5e0!3m2!1sen!2sus!4v1648675416987!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Dealership Location"
          />
        </MapContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default DealershipContact;
